import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import { handleLogout } from "../../config/axios";
import decryptData from "../../decrypt";

interface MenuBoxProps {
  onReady?: () => void;
}

interface MenuItem {
  name: string;
  image: string;
  page: { slug: string };
}

export default function MenuBox({ onReady }: MenuBoxProps) {
  const [menu, setMenu] = useState<MenuItem[]>([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const navigate = useNavigate();

  const categories = {
    Analisi: ["Fatturato"],
    Clienti: ["Anagrafica Clienti", "Fido Clienti"],
    Operazioni: ["Ordini", "BackOrder", "Appuntamenti"],
    Dati: ["Disponibilita", "Documenti"],
  };

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        const request = await fetch("/nodeapi/menu", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });

        if (request.status === 401 || request.status === 403 || request.status === 500) {
          handleLogout();
          return;
        }

        const response = await request.json();
        if (!request.ok) throw new Error(response.errorMessage);
        const decrypted = decryptData(response.encryptedData, response.encryptedKey, response.iv);
        const menus: MenuItem[] = JSON.parse(decrypted);
        const modifiedMenu = menus.map(item => ({
          ...item,
          size: item.name === "Documenti" ? "normal" : "normal"
        })).filter(item => item.name !== "Aggiornamento Dati");
        
        setMenu(modifiedMenu);
        onReady?.();
      } catch (e) {
        console.error(e);
        onReady?.();
      }
    };

    fetchMenu();
  }, [navigate, onReady]);

  const filteredMenu =
    activeCategory === "all"
      ? menu
      : menu.filter((item) =>
          Object.entries(categories).some(
            ([category, items]) =>
              category === activeCategory && items.includes(item.name)
          )
        );

  return (
    <div className="relative w-full max-w-screen-xl mx-auto px-4 py-8">
      {/* Category Filter */}
      <div className="mb-8 flex flex-wrap justify-center gap-2 px-4">
        <button
          onClick={() => setActiveCategory("all")}
          className={`px-4 py-2 rounded-full text-sm font-medium transition ${
            activeCategory === "all"
              ? "bg-blue-600 text-white shadow-lg"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-neutral-700 dark:text-gray-300 dark:hover:bg-neutral-600"
          }`}
        >
          Tutti
        </button>
        {Object.keys(categories).map((category) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition ${
              activeCategory === category
                ? "bg-blue-600 text-white shadow-lg"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-neutral-700 dark:text-gray-300 dark:hover:bg-neutral-600"
            }`}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Swiper Slider */}
      <div className="relative">
        <Swiper
          slidesPerView={1}
          spaceBetween={20}
          navigation={{
            nextEl: ".custom-next",
            prevEl: ".custom-prev",
          }}
          pagination={{ clickable: true }}
          breakpoints={{
            640: { slidesPerView: 2 },
            768: { slidesPerView: 3 },
            1024: { slidesPerView: 4 },
          }}
          modules={[Navigation, Pagination]}
          className="mySwiper"
        >
          {filteredMenu.map((item, index) => (
            <SwiperSlide key={index}>
              <Link to={`/${item.page.slug === "/" ? "" : item.page.slug}`}>
                <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-blue-200 dark:border-neutral-600 shadow-lg p-6 h-[200px] flex flex-col items-center justify-center transform transition-all duration-300 hover:scale-105">
                  <div className="w-16 h-16 mb-4 rounded-full border border-blue-600 dark:border-blue-400 bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                    <span className="material-symbols-outlined text-3xl text-blue-600 dark:text-blue-400" translate="no">
                      {item.image || '📁'}
                    </span>
                  </div>
                  <h3 className="text-center text-sm font-medium text-neutral-800 dark:text-neutral-200">
                    {item.name}
                  </h3>
                </div>
              </Link>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* Custom Navigation Buttons */}
        <button className="border border-blue-200 dark:border-neutral-600 custom-prev absolute top-1/2 left-2 sm:-left-12 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-white dark:bg-neutral-700 rounded-full shadow-lg hover:scale-110 transition">
          <span className="material-symbols-outlined text-blue-600 dark:text-blue-300" translate="no">
            chevron_left
          </span>
        </button>
        <button className="border border-blue-200 dark:border-neutral-600 custom-next absolute top-1/2 right-2 sm:-right-12 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-white dark:bg-neutral-700 rounded-full shadow-lg hover:scale-110 transition">
          <span className="material-symbols-outlined text-blue-600 dark:text-blue-300" translate="no">
            chevron_right
          </span>
        </button>
      </div>
    </div>
  );
}
